"use client"

/**
 * Leo apply motion: phased handoff from hero chips to thread compose, then
 * field receive (highlight + content settle) on apply-text.
 */

import * as React from "react"
import { useReducedMotion } from "motion/react"

import { LeoThinkingWidget } from "@/components/leo-thinking-widget"
import { cn } from "@/lib/utils"
import {
  LEO_APPLY_FOCUS_ATTR,
  LEO_APPLY_STAGGER_MS,
  leoApplyAnnouncement,
  leoApplySelector,
  type LeoApplyAnchor,
  type LeoApplyGhosts,
} from "@/lib/question-quality/leo-apply-targets"

export type LeoApplyCtaMotion = "working" | "answered"

export type LeoPhase =
  | "idle"
  | "acknowledge"
  | "handoff"
  | "compose"
  | "apply"
  | "resolve"

type ReceiveTarget = {
  anchor: LeoApplyAnchor
  delay: number
}

type ReceiveRun = {
  id: number
  targets: ReceiveTarget[]
}

type LeoApplyMotionApi = {
  phase: LeoPhase
  captureSource: (el: HTMLElement, key: string) => void
  /** Canvas apply only — no CTA working state. Used when the thread owns thinking. */
  armApplyKey: (key: string, onSettled?: () => void) => void
  beginHandoff: () => void
  beginCompose: () => void
  endHandoff: () => void
  resetMotion: () => void
  playApply: (
    anchors: LeoApplyAnchor[],
    ghosts?: LeoApplyGhosts,
    nextTexts?: LeoApplyGhosts,
  ) => void
  applyingKey: string | null
  applyingMotion: LeoApplyCtaMotion | null
  receive: ReceiveRun | null
}

/** Brief beat so the tapped chip shows working before Ask Leo opens. */
export const LEO_ACKNOWLEDGE_MS = 280

const RECEIVE_MS = 520
const REDUCE_RECEIVE_MS = 180
/** CTA holds filled + disabled through this beat before motion resets. */
export const LEO_APPLY_ANSWERED_MS = 480

const ANSWERED_MS = LEO_APPLY_ANSWERED_MS

/** When canvas receive finishes and the tapped CTA enters the answered state. */
export function leoApplyAnsweredDelayMs(reduceMotion = false): number {
  return reduceMotion ? REDUCE_RECEIVE_MS : RECEIVE_MS
}

const noopApi: LeoApplyMotionApi = {
  phase: "idle",
  captureSource: () => {},
  armApplyKey: () => {},
  beginHandoff: () => {},
  beginCompose: () => {},
  endHandoff: () => {},
  resetMotion: () => {},
  playApply: () => {},
  applyingKey: null,
  applyingMotion: null,
  receive: null,
}

const LeoApplyMotionContext = React.createContext<LeoApplyMotionApi>(noopApi)

const LeoApplyFocusContext = React.createContext<LeoApplyAnchor | null>(null)

export function LeoApplyFocusProvider({
  anchor,
  children,
}: {
  anchor: LeoApplyAnchor | null
  children: React.ReactNode
}) {
  return (
    <LeoApplyFocusContext.Provider value={anchor}>
      {children}
    </LeoApplyFocusContext.Provider>
  )
}

export function useLeoApplyFocusAnchor() {
  return React.useContext(LeoApplyFocusContext)
}

export const LEO_APPLY_FIELD_CLASS = "scroll-mt-6"

const LEO_APPLY_OPTION_WASH =
  "bg-[linear-gradient(180deg,color-mix(in_oklch,var(--brand-color)_12%,transparent),color-mix(in_oklch,var(--brand-color)_4%,transparent))]"

function LeoApplyOptionReceiveChrome() {
  return (
    <div
      className="pointer-events-none absolute inset-0 z-[2] overflow-hidden rounded-[inherit]"
      aria-hidden="true"
    >
      <span
        className={cn("absolute inset-0 rounded-[inherit]", LEO_APPLY_OPTION_WASH)}
      />
      <span className="absolute inset-x-0 bottom-0 h-0.5 bg-brand/55" />
      <LeoThinkingWidget
        content="loader"
        size="sm"
        className="absolute end-1.5 top-1/2 -translate-y-1/2 bg-transparent p-0"
      />
    </div>
  )
}

export function useLeoApplyMotion(): LeoApplyMotionApi {
  return React.useContext(LeoApplyMotionContext)
}

export function LeoApplyHeroFrame({ children }: { children: React.ReactNode }) {
  const { phase } = useLeoApplyMotion()
  const dim = phase === "handoff" || phase === "compose"
  return (
    <div
      className={cn(
        "transition-opacity duration-300 ease-out",
        dim && "pointer-events-none opacity-55",
      )}
      data-leo-hero-phase={phase}
      aria-hidden={dim ? true : undefined}
    >
      {children}
    </div>
  )
}

export function LeoApplyMotionProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const reduceMotion = useReducedMotion() ?? false
  const [phase, setPhase] = React.useState<LeoPhase>("idle")
  const [applyingKey, setApplyingKey] = React.useState<string | null>(null)
  const [applyingMotion, setApplyingMotion] =
    React.useState<LeoApplyCtaMotion | null>(null)
  const [receive, setReceive] = React.useState<ReceiveRun | null>(null)
  const [announcement, setAnnouncement] = React.useState("")
  const keyRef = React.useRef<string | null>(null)
  const onApplySettledRef = React.useRef<(() => void) | null>(null)
  const runId = React.useRef(0)
  const receiveTimer = React.useRef(0)
  const answeredTimer = React.useRef(0)
  const acknowledgeTimer = React.useRef(0)

  const finishAnswered = React.useCallback((key: string | null) => {
    onApplySettledRef.current?.()
    onApplySettledRef.current = null
    setApplyingMotion("answered")
    setPhase("resolve")
    window.clearTimeout(answeredTimer.current)
    answeredTimer.current = window.setTimeout(() => {
      setApplyingKey((current) => (current === key ? null : current))
      setApplyingMotion(null)
      setPhase("idle")
    }, ANSWERED_MS)
  }, [])

  const armApplyKey = React.useCallback(
    (key: string, onSettled?: () => void) => {
      keyRef.current = key
      onApplySettledRef.current = onSettled ?? null
    },
    [],
  )

  const captureSource = React.useCallback((_el: HTMLElement, key: string) => {
    window.clearTimeout(acknowledgeTimer.current)
    window.clearTimeout(answeredTimer.current)
    keyRef.current = key
    setApplyingKey(key)
    setApplyingMotion("working")
    setPhase("acknowledge")
  }, [])

  const beginHandoff = React.useCallback(() => {
    setPhase((current) =>
      current === "acknowledge" || current === "idle" ? "handoff" : current,
    )
    setApplyingMotion((current) => current ?? "working")
  }, [])

  const beginCompose = React.useCallback(() => {
    setPhase((current) =>
      current === "handoff" || current === "acknowledge" ? "compose" : current,
    )
  }, [])

  const endHandoff = React.useCallback(() => {
    const key = keyRef.current
    setPhase((current) => {
      if (current !== "handoff" && current !== "compose") return current
      finishAnswered(key)
      keyRef.current = null
      return "resolve"
    })
  }, [finishAnswered])

  const resetMotion = React.useCallback(() => {
    window.clearTimeout(receiveTimer.current)
    window.clearTimeout(answeredTimer.current)
    window.clearTimeout(acknowledgeTimer.current)
    keyRef.current = null
    onApplySettledRef.current = null
    setPhase("idle")
    setApplyingKey(null)
    setApplyingMotion(null)
    setReceive(null)
    setAnnouncement("")
  }, [])

  const playApply = React.useCallback(
    (
      anchors: LeoApplyAnchor[],
      _ghosts: LeoApplyGhosts = {},
      _nextTexts: LeoApplyGhosts = {},
    ) => {
      const key = keyRef.current
      keyRef.current = null
      window.clearTimeout(receiveTimer.current)
      window.clearTimeout(answeredTimer.current)

      if (anchors.length === 0) return

      setPhase("apply")
      setAnnouncement(leoApplyAnnouncement(anchors))

      const run = (attempt = 0) => {
        const missing = anchors.some(
          (anchor) => !document.querySelector(leoApplySelector(anchor)),
        )
        if (missing && attempt < 2) {
          window.requestAnimationFrame(() => run(attempt + 1))
          return
        }
        const targets = anchors.map((anchor, index) => ({
          anchor,
          delay: index * LEO_APPLY_STAGGER_MS,
        }))
        const landing = document.querySelector(leoApplySelector(targets[0]?.anchor ?? ""))
        if (landing instanceof HTMLElement) {
          landing.scrollIntoView({ block: "nearest", behavior: "auto" })
        }
        runId.current += 1
        setReceive({ id: runId.current, targets })

        const lastDelay = targets.reduce(
          (max, target) => Math.max(max, target.delay),
          0,
        )
        const receiveMs = reduceMotion ? REDUCE_RECEIVE_MS : RECEIVE_MS
        receiveTimer.current = window.setTimeout(() => {
          setReceive(null)
          finishAnswered(key)
        }, lastDelay + receiveMs)
      }

      window.requestAnimationFrame(() => window.requestAnimationFrame(run))
    },
    [finishAnswered, reduceMotion],
  )

  React.useEffect(() => {
    return () => {
      window.clearTimeout(receiveTimer.current)
      window.clearTimeout(answeredTimer.current)
      window.clearTimeout(acknowledgeTimer.current)
    }
  }, [])

  const value = React.useMemo(
    () => ({
      phase,
      captureSource,
      armApplyKey,
      beginHandoff,
      beginCompose,
      endHandoff,
      resetMotion,
      playApply,
      applyingKey,
      applyingMotion,
      receive,
    }),
    [
      applyingKey,
      applyingMotion,
      armApplyKey,
      beginCompose,
      beginHandoff,
      captureSource,
      endHandoff,
      resetMotion,
      phase,
      playApply,
      receive,
    ],
  )

  return (
    <LeoApplyMotionContext.Provider value={value}>
      {children}
      <p className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {announcement}
      </p>
    </LeoApplyMotionContext.Provider>
  )
}

export const LeoApplyField = React.forwardRef<
  HTMLDivElement,
  {
    anchor: LeoApplyAnchor
    className?: string
    children: React.ReactNode
  } & Omit<React.HTMLAttributes<HTMLDivElement>, "children">
>(function LeoApplyField(
  { anchor, className, children, style, ...rest },
  ref,
) {
  const { receive } = useLeoApplyMotion()
  const focusAnchor = useLeoApplyFocusAnchor()
  const hit = receive?.targets.find((target) => target.anchor === anchor) ?? null
  const optionReceive = Boolean(hit && anchor.startsWith("option:"))
  const focused = focusAnchor === anchor

  return (
    <div
      ref={ref}
      className={cn("relative rounded-xl", LEO_APPLY_FIELD_CLASS, className)}
      data-leo-apply={anchor}
      {...{ [LEO_APPLY_FOCUS_ATTR]: focused ? "" : undefined }}
      aria-current={focused ? "true" : undefined}
      data-leo-receiving={hit ? "" : undefined}
      style={{
        ...style,
        ...(hit
          ? ({ ["--leo-apply-delay"]: `${hit.delay}ms` } as React.CSSProperties)
          : null),
      }}
      {...rest}
    >
      {optionReceive ? <LeoApplyOptionReceiveChrome /> : null}
      {children}
    </div>
  )
})

LeoApplyField.displayName = "LeoApplyField"
